/* eslint-disable react/no-unknown-property */
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { useMemo, useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { scroller } from 'react-scroll';
import logoImg from './logo2.png';

const STYLE = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Space+Mono:wght@400;700&display=swap');

  .ag-wrapper {
    position: relative; width: 100%; height: 100%;
    overflow: hidden; background: #FFAA00; cursor: none;
  }

  .ag-topo {
    position: absolute; inset: 0; display: flex;
    align-items: center; justify-content: center;
    z-index: 1; pointer-events: none; will-change: transform;
    transition: transform 0.12s linear;
  }
  .ag-topo-ring { position: absolute; }
  .ag-topo-ring:nth-child(1) { width: 92vmin; height: 86vmin; background: #FF9800; opacity: 0.5; border-radius: 38% 62% 55% 45%/45% 48% 52% 55%; animation: ag-m1 10s ease-in-out infinite, ag-rot-cw 28s linear infinite; }
  .ag-topo-ring:nth-child(2) { width: 75vmin; height: 70vmin; background: #FFB300; opacity: 0.6; border-radius: 55% 45% 42% 58%/50% 55% 45% 50%; animation: ag-m2 12s ease-in-out infinite, ag-rot-ccw 22s linear infinite; }
  .ag-topo-ring:nth-child(3) { width: 58vmin; height: 54vmin; background: #FFC107; opacity: 0.65; border-radius: 45% 55% 60% 40%/55% 42% 58% 45%; animation: ag-m3 9s ease-in-out infinite, ag-rot-cw 18s linear infinite; }
  .ag-topo-ring:nth-child(4) { width: 42vmin; height: 39vmin; background: #FFD54F; opacity: 0.72; border-radius: 60% 40% 45% 55%/42% 60% 40% 58%; animation: ag-m1 14s ease-in-out infinite reverse, ag-rot-ccw 14s linear infinite; }
  .ag-topo-ring:nth-child(5) { width: 27vmin; height: 25vmin; background: #FFE57F; opacity: 0.92; border-radius: 42% 58% 50% 50%/58% 42% 56% 44%; animation: ag-m2 8s ease-in-out infinite reverse, ag-rot-cw 10s linear infinite; }

  @keyframes ag-m1 {
    0%,100% { border-radius: 38% 62% 55% 45%/45% 48% 52% 55%; transform: scale(1); }
    25%      { border-radius: 55% 45% 62% 38%/50% 55% 45% 50%; transform: scale(1.02); }
    50%      { border-radius: 45% 55% 40% 60%/60% 40% 55% 45%; transform: scale(0.98); }
    75%      { border-radius: 62% 38% 48% 52%/42% 58% 50% 50%; transform: scale(1.01); }
  }
  @keyframes ag-m2 {
    0%,100% { border-radius: 55% 45% 42% 58%/50% 55% 45% 50%; transform: scale(1); }
    33%      { border-radius: 40% 60% 58% 42%/55% 42% 58% 45%; transform: scale(1.03); }
    66%      { border-radius: 60% 40% 45% 55%/44% 60% 42% 56%; transform: scale(0.97); }
  }
  @keyframes ag-m3 {
    0%,100% { border-radius: 45% 55% 60% 40%/55% 42% 58% 45%; transform: scale(1); }
    50%      { border-radius: 60% 40% 42% 58%/44% 58% 45% 55%; transform: scale(1.04); }
  }
  @keyframes ag-rot-cw  { from { rotate:  0deg; } to { rotate:  360deg; } }
  @keyframes ag-rot-ccw { from { rotate:  0deg; } to { rotate: -360deg; } }

  .ag-canvas-layer { position: absolute; inset: 0; z-index: 2; pointer-events: none; }

  /* ── Glowy Yellow Cursor ── */
  .ag-cursor {
    position: absolute;
    width: 12px; height: 12px;
    background: #FFD600;
    border-radius: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none; z-index: 20;
    box-shadow:
      0 0 6px 2px #FFD600,
      0 0 18px 6px rgba(255, 214, 0, 0.6),
      0 0 36px 12px rgba(255, 214, 0, 0.25);
    animation: ag-cursor-pulse 1.8s ease-in-out infinite;
  }
  @keyframes ag-cursor-pulse {
    0%, 100% {
      box-shadow: 0 0 6px 2px #FFD600, 0 0 18px 6px rgba(255,214,0,0.6), 0 0 36px 12px rgba(255,214,0,0.25);
      transform: translate(-50%, -50%) scale(1);
    }
    50% {
      box-shadow: 0 0 10px 4px #FFD600, 0 0 28px 10px rgba(255,214,0,0.7), 0 0 52px 18px rgba(255,214,0,0.3);
      transform: translate(-50%, -50%) scale(1.15);
    }
  }

  /* ── Floating Magnetic Icon ── */
  .ag-float-text {
    position: absolute;
    transform: translate(-50%, -50%);
    pointer-events: none;
    z-index: 5;
    will-change: left, top;
  }
  .ag-float-icon {
    width: 110px; height: 110px;
    border-radius: 28px;
    background: rgba(255, 255, 255, 0.12);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border: 2px solid rgba(255, 255, 255, 0.3);
    display: flex; align-items: center; justify-content: center;
    animation: ag-icon-breathe 4s ease-in-out infinite;
    box-shadow:
      0 0 0 6px rgba(255, 214, 0, 0.15),
      0 0 0 12px rgba(255, 214, 0, 0.07),
      0 12px 48px rgba(0, 0, 0, 0.45),
      0 2px 8px rgba(0,0,0,0.3);
  }
  .ag-float-icon img {
    width: 80px; height: 80px;
    object-fit: contain;
    animation: ag-icon-shine 3s ease-in-out infinite;
  }
  @keyframes ag-icon-shine {
    0%,100% {
      filter: drop-shadow(0 0 8px rgba(255,214,0,1)) drop-shadow(0 0 22px rgba(255,214,0,0.85)) drop-shadow(0 0 44px rgba(255,214,0,0.5)) brightness(1.25) contrast(1.05);
    }
    50% {
      filter: drop-shadow(0 0 14px rgba(255,214,0,1)) drop-shadow(0 0 38px rgba(255,214,0,0.95)) drop-shadow(0 0 70px rgba(255,214,0,0.65)) brightness(1.5) contrast(1.1);
    }
  }
  @keyframes ag-icon-breathe {
    0%, 100% {
      transform: scale(1) translateY(0px);
      box-shadow: 0 0 0 6px rgba(255,214,0,0.15), 0 0 0 12px rgba(255,214,0,0.07), 0 12px 48px rgba(0,0,0,0.45), 0 2px 8px rgba(0,0,0,0.3);
    }
    50% {
      transform: scale(1.06) translateY(-8px);
      box-shadow: 0 0 0 10px rgba(255,214,0,0.2), 0 0 0 20px rgba(255,214,0,0.08), 0 24px 64px rgba(0,0,0,0.5), 0 4px 16px rgba(0,0,0,0.3);
    }
  }

  /* ── Scroll button ── */
  .ag-scroll-btn {
    position: absolute; bottom: 48px; left: 50%; transform: translateX(-50%);
    z-index: 10; background: #fff; border: 3px solid #3355FF; border-radius: 999px;
    padding: 14px 40px; font-family: 'Bebas Neue', monospace; font-size: 18px;
    letter-spacing: 0.18em; color: #3355FF; cursor: pointer;
    display: flex; align-items: center; gap: 12px;
    transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
    box-shadow: 4px 4px 0 #3355FF;
    animation: ag-btn-float 3s ease-in-out infinite; white-space: nowrap;
  }
  .ag-scroll-btn:hover { background: #3355FF; color: #fff; transform: translateX(-50%) translateY(-3px); box-shadow: 6px 6px 0 #001aaa; }
  .ag-scroll-btn svg { width: 18px; height: 18px; fill: currentColor; animation: ag-arrow 1.5s ease-in-out infinite; }
  @keyframes ag-btn-float { 0%,100%{bottom:48px}50%{bottom:56px} }
  @keyframes ag-arrow { 0%,100%{transform:translateY(0)}50%{transform:translateY(4px)} }

  /* ── Video modal ── */
  .ag-video-overlay { position:fixed;inset:0;background:rgba(0,0,0,0.88);z-index:1000;display:flex;align-items:center;justify-content:center;animation:ag-fade 0.3s ease; }
  @keyframes ag-fade { from{opacity:0}to{opacity:1} }
  .ag-video-box { position:relative;width:min(900px,92vw);aspect-ratio:16/9;background:#000;border-radius:16px;overflow:hidden;box-shadow:0 32px 80px rgba(0,0,0,0.6); }
  .ag-video-box iframe { width:100%;height:100%;border:none; }
  .ag-video-close { position:absolute;top:-44px;right:0;background:none;border:none;color:#fff;font-family:'Bebas Neue',monospace;font-size:22px;letter-spacing:0.1em;cursor:pointer;opacity:0.8;transition:opacity 0.2s; }
  .ag-video-close:hover{opacity:1}

  /* ── Bottom ticker ── */
  .ag-bottom { position:absolute;bottom:0;left:0;right:0;height:36px;z-index:8;display:flex;align-items:center;pointer-events:none; }
  .ag-ticker { flex:1;overflow:hidden;mask-image:linear-gradient(to right,transparent,black 6%,black 94%,transparent); }
  .ag-ticker-inner { display:flex;gap:28px;animation:ag-scroll 22s linear infinite;white-space:nowrap; }
  .ag-ticker-item { font-family:'Space Mono',monospace;font-size:9px;font-weight:700;letter-spacing:0.2em;color:rgba(0,0,0,0.3);text-transform:uppercase;transition:color 0.2s,text-shadow 0.2s;cursor:default; }
  .ag-ticker-item:hover { color:#1B2FE8; text-shadow:0 0 10px rgba(27,47,232,0.6),0 0 22px rgba(27,47,232,0.3); }
  @keyframes ag-scroll { from{transform:translateX(0)}to{transform:translateX(-50%)} }

  /* ── Nav corners ── */
  .ag-nav-left {
    position:absolute;top:28px;left:36px;
    font-family:'Bebas Neue',monospace;font-size:clamp(20px,2.5vw,28px);
    letter-spacing:0.14em;color:#fff;z-index:10;
    text-shadow:0 2px 12px rgba(0,0,0,0.2);
  }
  .ag-nav-right {
    position:absolute;top:28px;right:36px;
    font-family:'Bebas Neue',monospace;font-size:clamp(20px,2.5vw,28px);
    letter-spacing:0.14em;color:rgba(255,255,255,0.7);z-index:10;
    text-shadow:0 2px 12px rgba(0,0,0,0.2);
    pointer-events:none;
  }

  /* ══ MENU OVERLAY ══ */
  .menu-overlay { position:fixed;inset:0;background:#FFD600;z-index:2000;display:flex;flex-direction:column;align-items:center;justify-content:center;overflow:hidden;animation:menuIn 0.45s cubic-bezier(0.77,0,0.175,1) forwards; }
  @keyframes menuIn { from{clip-path:circle(0% at 0% 0%)}to{clip-path:circle(150% at 0% 0%)} }
  .menu-overlay.closing { animation:menuOut 0.35s cubic-bezier(0.77,0,0.175,1) forwards; }
  @keyframes menuOut { from{clip-path:circle(150% at 0% 0%)}to{clip-path:circle(0% at 0% 0%)} }
  .menu-close { position:absolute;top:32px;left:36px;background:none;border:none;font-family:'Bebas Neue',monospace;font-size:26px;letter-spacing:0.1em;cursor:pointer;color:#1a1a1a;display:flex;align-items:center;gap:8px;z-index:10;transition:transform 0.2s; }
  .menu-close:hover{transform:rotate(90deg) scale(1.1)}
  .menu-links { display:flex;flex-direction:column;align-items:center;justify-content:center;gap:0;width:100%;padding:60px 20px 20px; }
  .menu-link-item { font-family:'Bebas Neue',monospace;font-size:clamp(44px,8vw,88px);line-height:1;letter-spacing:0.02em;text-decoration:none;color:#1B2FE8;display:block;text-align:center;transition:transform 0.25s cubic-bezier(.34,1.56,.64,1),color 0.2s;opacity:0;transform:translateY(30px);animation:mItemIn 0.5s cubic-bezier(.34,1.56,.64,1) forwards; }
  .menu-link-item:hover{transform:skewX(-8deg) scale(1.08)!important;color:#1a1a1a}
  .menu-link-item:nth-child(1){animation-delay:0.15s}.menu-link-item:nth-child(2){animation-delay:0.22s}.menu-link-item:nth-child(3){animation-delay:0.29s}.menu-link-item:nth-child(4){animation-delay:0.36s}.menu-link-item:nth-child(5){animation-delay:0.43s}.menu-link-item:nth-child(6){animation-delay:0.50s}
  @keyframes mItemIn { 0%{opacity:0;transform:translateY(40px) skewY(4deg)}60%{opacity:1;transform:translateY(-6px) skewY(-1deg)}100%{opacity:1;transform:translateY(0) skewY(0deg)} }
  .menu-auth { display:flex;gap:16px;margin-top:20px;opacity:0;animation:mItemIn 0.5s cubic-bezier(.34,1.56,.64,1) 0.52s forwards; }
  .auth-btn { font-family:'Bebas Neue',monospace;font-size:clamp(18px,3vw,26px);letter-spacing:0.1em;text-decoration:none;color:#1a1a1a;border:3px solid #1a1a1a;border-radius:999px;padding:10px 32px;display:inline-block;transition:transform 0.2s cubic-bezier(.34,1.56,.64,1),box-shadow 0.2s;box-shadow:3px 3px 0 #1a1a1a; }
  .auth-btn:hover{transform:translateY(-3px);box-shadow:5px 5px 0 #1a1a1a}
  .auth-btn.highlight{background:#1a1a1a;color:#FFD600}
  .auth-btn.highlight:hover{background:#1B2FE8;border-color:#1B2FE8;color:#FFD600}
  .menu-deco-tl{position:absolute;top:20px;right:36px;font-family:'Bebas Neue',monospace;font-size:22px;letter-spacing:0.1em;color:rgba(27,47,232,0.3);pointer-events:none}
  .menu-deco-br{position:absolute;bottom:24px;right:40px;font-size:48px;pointer-events:none;animation:pulse 3s ease-in-out infinite}
  .menu-deco-bl{position:absolute;bottom:24px;left:40px;font-size:36px;pointer-events:none;animation:pulse 3s ease-in-out infinite;animation-delay:1s}
  @keyframes pulse{0%,100%{transform:scale(1)}50%{transform:scale(1.05)}}
`;

function injectStyle(id, css) {
  if (typeof document === 'undefined') return;
  if (document.getElementById(id)) return;
  const el = document.createElement('style');
  el.id = id; el.textContent = css;
  document.head.appendChild(el);
}

const TICKERS = [
  'Monospace','◆','Type Design','◆','Fixed Width','◆','Code & Craft','◆','Precision','◆','Developer Tools','◆',
  'Monospace','◆','Type Design','◆','Fixed Width','◆','Code & Craft','◆','Precision','◆','Developer Tools','◆',
];

const ParticleField = ({ mouseWorldRef, count, magnetRadius, ringRadius, waveSpeed, waveAmplitude, particleSize, lerpSpeed, color, particleVariance, rotationSpeed, depthFactor, pulseSpeed, particleShape, fieldStrength }) => {
  const meshRef = useRef(null);
  const glowRef = useRef(null);
  const { viewport } = useThree();
  const dummy = useMemo(() => new THREE.Object3D(), []);

  const particles = useMemo(() => {
    const temp = [];
    const w = viewport.width || 100; const h = viewport.height || 100;
    for (let i = 0; i < count; i++) {
      const x = (Math.random() - 0.5) * w; const y = (Math.random() - 0.5) * h; const z = (Math.random() - 0.5) * 20;
      temp.push({ t: Math.random() * 100, speed: 0.01 + Math.random() / 200, mx: x, my: y, mz: z, cx: x, cy: y, cz: z, randomRadiusOffset: (Math.random() - 0.5) * 2 });
    }
    return temp;
  }, [count, viewport.width, viewport.height]);

  const glowColor = useMemo(() => {
    try { const c = new THREE.Color(color); c.lerp(new THREE.Color('#ffffff'), 0.4); return c; }
    catch { return new THREE.Color('#ffe066'); }
  }, [color]);

  useFrame(state => {
    const mesh = meshRef.current; const glow = glowRef.current;
    if (!mesh) return;
    const targetX = mouseWorldRef.current.x; const targetY = mouseWorldRef.current.y;
    const globalRotation = state.clock.getElapsedTime() * rotationSpeed;
    particles.forEach((particle, i) => {
      particle.t += particle.speed / 2;
      const t = particle.t; const { mx, my, mz, cz, randomRadiusOffset } = particle;
      const projFactor = 1 - cz / 50;
      const ptX = targetX * projFactor; const ptY = targetY * projFactor;
      const dx = mx - ptX; const dy = my - ptY; const dist = Math.hypot(dx, dy);
      let tPos = { x: mx, y: my, z: mz * depthFactor };
      if (dist < magnetRadius) {
        const angle = Math.atan2(dy, dx) + globalRotation;
        const wave = Math.sin(t * waveSpeed + angle) * (0.5 * waveAmplitude);
        const dev = randomRadiusOffset * (5 / (fieldStrength + 0.1));
        const r = ringRadius + wave + dev;
        tPos = { x: ptX + r * Math.cos(angle), y: ptY + r * Math.sin(angle), z: mz * depthFactor + Math.sin(t) * waveAmplitude * depthFactor };
      }
      particle.cx += (tPos.x - particle.cx) * lerpSpeed;
      particle.cy += (tPos.y - particle.cy) * lerpSpeed;
      particle.cz += (tPos.z - particle.cz) * lerpSpeed;
      dummy.position.set(particle.cx, particle.cy, particle.cz);
      dummy.lookAt(ptX, ptY, particle.cz); dummy.rotateX(Math.PI / 2);
      const curDist = Math.hypot(particle.cx - ptX, particle.cy - ptY);
      const fromRing = Math.abs(curDist - ringRadius);
      const sf = Math.max(0, Math.min(1, 1 - fromRing / 10));
      const finalScale = sf * (0.8 + Math.sin(t * pulseSpeed) * 0.2 * particleVariance) * particleSize;
      dummy.scale.set(finalScale, finalScale, finalScale); dummy.updateMatrix(); mesh.setMatrixAt(i, dummy.matrix);
      if (glow) { const gs = finalScale * 2.4; dummy.scale.set(gs, gs, gs); dummy.updateMatrix(); glow.setMatrixAt(i, dummy.matrix); }
    });
    mesh.instanceMatrix.needsUpdate = true;
    if (glow) glow.instanceMatrix.needsUpdate = true;
  });

  const Geom = () => {
    switch (particleShape) {
      case 'capsule': return <capsuleGeometry args={[0.1, 0.4, 4, 8]} />;
      case 'box': return <boxGeometry args={[0.3, 0.3, 0.3]} />;
      case 'tetrahedron': return <tetrahedronGeometry args={[0.3]} />;
      default: return <sphereGeometry args={[0.2, 16, 16]} />;
    }
  };

  return (
    <>
      <instancedMesh ref={glowRef} args={[undefined, undefined, count]} renderOrder={0}>
        <Geom /><meshBasicMaterial color={glowColor} transparent opacity={0.10} depthWrite={false} blending={THREE.AdditiveBlending} />
      </instancedMesh>
      <instancedMesh ref={meshRef} args={[undefined, undefined, count]} renderOrder={1}>
        <Geom /><meshBasicMaterial color={color} transparent opacity={0.85} blending={THREE.AdditiveBlending} depthWrite={false} />
      </instancedMesh>
    </>
  );
};

const VideoModal = ({ onClose }) => (
  <div className="ag-video-overlay" onClick={onClose}>
    <div className="ag-video-box" onClick={e => e.stopPropagation()}>
      <button className="ag-video-close" onClick={onClose}>✕ CLOSE</button>
      <iframe src="https://www.youtube.com/embed/aircAruvnKk?autoplay=1&mute=0&rel=0" title="Monospace Video" allow="autoplay; fullscreen" allowFullScreen />
    </div>
  </div>
);
const MenuOverlay = ({ onClose }) => {
  const [closing, setClosing] = useState(false);
  const handleClose = () => { setClosing(true); setTimeout(onClose, 340); };
  const NAV_LINKS = [
    { to: 'home',     label: 'HOME'     },
    { to: 'about',    label: 'ABOUT'    },
    { to: 'support',  label: 'SUPPORT'  },
    { to: 'services', label: 'SERVICES' },
    { to: 'blog',     label: 'BLOG'     },
    { to: 'contact',  label: 'CONTACT'  },
  ];
  const handleNav = (sectionId) => {
    setClosing(true);
    setTimeout(() => {
      onClose();
      scroller.scrollTo(sectionId, { smooth: true, duration: 700, offset: 0 });
    }, 340);
  };
  return (
    <div className={`menu-overlay${closing ? ' closing' : ''}`}>
      <button className="menu-close" onClick={handleClose}>✕ CLOSE</button>
      <div className="menu-deco-tl">DIGITAL AGENCY</div>
      <div className="menu-deco-br">⌨️</div>
      <div className="menu-deco-bl">{ }</div>
      <nav className="menu-links">
        {NAV_LINKS.map((link, i) => (
          <button
            key={link.to}
            className="menu-link-item"
            style={{ background: 'none', border: 'none', cursor: 'pointer', animationDelay: `${0.15 + i * 0.07}s` }}
            onClick={() => handleNav(link.to)}
          >
            {link.label}
          </button>
        ))}
      </nav>
    </div>
  );
};

/* ─── Main Antigravity ─── */
const Antigravity = (props) => {
  const {
    count=300, magnetRadius=10, ringRadius=10, waveSpeed=0.4, waveAmplitude=1,
    particleSize=2, lerpSpeed=0.1, color='#FFD600', autoAnimate=false,
    particleVariance=1, rotationSpeed=0, depthFactor=1, pulseSpeed=3,
    particleShape='sphere', fieldStrength=10,
  } = props;

  useEffect(() => { injectStyle('ag-styles', STYLE); }, []);

  const [videoOpen, setVideoOpen] = useState(false);
  const [menuOpen,  setMenuOpen]  = useState(false);

  const wrapperRef    = useRef(null);
  const topoRef       = useRef(null);
  const cursorRef     = useRef(null);
  const floatTextRef  = useRef(null);   // ← magnetic text ref

  // Two lerp positions: fast (cursor dot) and slower (float text, slightly behind)
  const badgePos      = useRef({ x: 0, y: 0 });
  const textPos       = useRef({ x: 0, y: 0 });
  const targetPos     = useRef({ x: 0, y: 0 });
  const mouseWorldRef = useRef({ x: 0, y: 0 });
  const rafRef        = useRef(null);
  const lastMouseTime = useRef(Date.now());

  useEffect(() => {
    const el = wrapperRef.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const cx = r.width / 2; const cy = r.height / 2;
    badgePos.current  = { x: cx, y: cy };
    textPos.current   = { x: cx, y: cy };
    targetPos.current = { x: cx, y: cy };
    if (floatTextRef.current) {
      floatTextRef.current.style.left = cx + 'px';
      floatTextRef.current.style.top  = cy + 'px';
    }
  }, []);

  useEffect(() => {
    const el = wrapperRef.current; if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      lastMouseTime.current = Date.now();
      const mx = e.clientX - r.left;
      const my = e.clientY - r.top;
      targetPos.current = { x: mx, y: my };
      if (cursorRef.current) {
        cursorRef.current.style.left = mx + 'px';
        cursorRef.current.style.top  = my + 'px';
      }
    };
    el.addEventListener('mousemove', onMove);
    return () => el.removeEventListener('mousemove', onMove);
  }, []);

  useEffect(() => {
    const el = wrapperRef.current; if (!el) return;
    const LERP_BADGE = 0.08;
    const LERP_TEXT  = 0.045;  // floaty, lags behind nicely

    const tick = () => {
      const r   = el.getBoundingClientRect();
      const now = Date.now();

      if (autoAnimate && now - lastMouseTime.current > 2000) {
        const t = now / 1000;
        targetPos.current = {
          x: r.width  / 2 + Math.sin(t * 0.5)     * (r.width  * 0.22),
          y: r.height / 2 + Math.sin(t * 0.5 * 2) * (r.height * 0.18),
        };
      }

      // Badge lerp (drives world position)
      badgePos.current.x += (targetPos.current.x - badgePos.current.x) * LERP_BADGE;
      badgePos.current.y += (targetPos.current.y - badgePos.current.y) * LERP_BADGE;

      // Floating text lerp (slower — magnetic lag)
      textPos.current.x += (targetPos.current.x - textPos.current.x) * LERP_TEXT;
      textPos.current.y += (targetPos.current.y - textPos.current.y) * LERP_TEXT;

      if (floatTextRef.current) {
        floatTextRef.current.style.left = textPos.current.x + 'px';
        floatTextRef.current.style.top  = textPos.current.y + 'px';
      }

      // Topo parallax
      if (topoRef.current) {
        const ox = (badgePos.current.x - r.width  / 2) * 0.05;
        const oy = (badgePos.current.y - r.height / 2) * 0.05;
        topoRef.current.style.transform = `translate(${ox}px, ${oy}px)`;
      }

      // World coords for Three.js
      const nx    = (badgePos.current.x / r.width)  * 2 - 1;
      const ny    = -((badgePos.current.y / r.height) * 2 - 1);
      const halfH = Math.tan((35 / 2) * (Math.PI / 180)) * 50;
      const halfW = halfH * (r.width / r.height);
      mouseWorldRef.current.x = nx * halfW;
      mouseWorldRef.current.y = ny * halfH;

      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [autoAnimate]);

  return (
    <div className="ag-wrapper" ref={wrapperRef} style={{ width: '100%', height: '100%' }}>

      {/* Glowy yellow cursor dot */}
      <div className="ag-cursor" ref={cursorRef} />

      {/* Top-left: MENU */}
      <div
        className="ag-nav-left"
        style={{ cursor: 'pointer', pointerEvents: 'all' }}
        onClick={() => setMenuOpen(true)}
      >
        MENU
      </div>

      {/* Top-right: brand word */}
      <div className="ag-nav-right">DIGITAL AGENCY</div>

      {/* Topo rings */}
      <div className="ag-topo" ref={topoRef}>
        {[0,1,2,3,4].map(i => <div key={i} className="ag-topo-ring" />)}
      </div>

      {/* Particles */}
      <div className="ag-canvas-layer">
        <Canvas
          camera={{ position: [0, 0, 50], fov: 35 }}
          gl={{ alpha: true, antialias: true }}
          style={{ background: 'transparent', width: '100%', height: '100%' }}
        >
          <ParticleField
            mouseWorldRef={mouseWorldRef} count={count} magnetRadius={magnetRadius}
            ringRadius={ringRadius} waveSpeed={waveSpeed} waveAmplitude={waveAmplitude}
            particleSize={particleSize} lerpSpeed={lerpSpeed} color={color}
            particleVariance={particleVariance} rotationSpeed={rotationSpeed}
            depthFactor={depthFactor} pulseSpeed={pulseSpeed}
            particleShape={particleShape} fieldStrength={fieldStrength}
          />
        </Canvas>
      </div>

      {/* ★ Magnetic floating icon ★ */}
      <div className="ag-float-text" ref={floatTextRef}>
        <div className="ag-float-icon">
          <img src={logoImg} alt="Digital Agency" />
        </div>
      </div>

      {/* Scroll CTA */}
      <button className="ag-scroll-btn" onClick={() => setVideoOpen(true)}>
        SCROLL TO DISCOVER
        <svg viewBox="0 0 24 24"><path d="M12 16l-6-6h12z"/></svg>
      </button>

      {/* Bottom ticker */}
      <div className="ag-bottom">
        <div className="ag-ticker">
          <div className="ag-ticker-inner">
            {TICKERS.map((item, i) => <span key={i} className="ag-ticker-item">{item}</span>)}
          </div>
        </div>
      </div>

      {videoOpen && <VideoModal onClose={() => setVideoOpen(false)} />}
      {menuOpen  && <MenuOverlay onClose={() => setMenuOpen(false)} />}
    </div>
  );
};

export default Antigravity;